if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'sleektheme:popup-art-journal-draft';

        this.setupArtJournalFlow();

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
      }

      handleOpen() {
        if (Shopify.designMode) return;

        const previewTest = new URLSearchParams(window.location.search).get('art_journal_test') === '1';

        if (this.hasSuccessfulSignup()) {
          this.show();
          this.renderSuccess();
          return;
        }

        if (previewTest) {
          setTimeout(() => {
            this.show();
          }, 750);
          return;
        }

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
            }, 30000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, this.show.bind(this));
            break;
          default:
            this.show();
            break;
        }
      }

      setupArtJournalFlow() {
        const blocks = this.querySelector('.subscription-popup__blocks');
        const title = this.querySelector('.subscription-popup__title');
        const textBlock = this.querySelector('.subscription-popup__text');
        const formWrap = this.querySelector('.subscription-popup__form');

        if (!blocks || !title || !textBlock || !formWrap) return;

        this.artJournal = { blocks, title, textBlock, formWrap };

        const possibleSocials = blocks.querySelectorAll('.list-social, .social-icons, [class*="social"]');
        possibleSocials.forEach((node) => {
          if (!node.closest('.subscription-popup__form')) node.hidden = true;
        });

        const actions = document.createElement('div');
        actions.className = 'art-journal-popup__actions';
        actions.innerHTML = `
          <button type="button" class="btn btn--primary w-full art-journal-popup__continue">
            YES, I’D LIKE TO SEE NEW WORKS FIRST
          </button>
          <button type="button" class="art-journal-popup__decline">
            No thanks, I’ll continue exploring.
          </button>
        `;
        blocks.insertBefore(actions, formWrap);
        this.artJournal.actions = actions;

        const support = document.createElement('div');
        support.className = 'art-journal-popup__supporting text-sm';
        support.innerHTML = 'By joining, you’ll receive my Art Journal, including new artworks, stories from the water, exhibitions and occasional collector news. You can unsubscribe at any time.';
        formWrap.insertAdjacentElement('afterend', support);
        support.hidden = true;
        this.artJournal.support = support;

        const form = formWrap.querySelector('form');
        if (form) {
          const tags = form.querySelector('input[name="contact[tags]"]');
          if (tags) tags.value = 'newsletter,Art Journal Subscriber,Art Journal — Popup';

          const email = form.querySelector('input[type="email"]');
          if (email) {
            email.placeholder = 'Email Address';
            email.setAttribute('aria-label', 'Email Address');

            const emailField = email.closest('.form-field');
            if (emailField && !form.querySelector('input[name="contact[first_name]"]')) {
              const firstNameField = document.createElement('div');
              firstNameField.className = 'form-field relative art-journal-popup__first-name';
              firstNameField.innerHTML = `
                <label class="form-label visually-hidden" for="ArtJournalFirstName">First Name</label>
                <input
                  id="ArtJournalFirstName"
                  type="text"
                  name="contact[first_name]"
                  class="form-control"
                  autocomplete="given-name"
                  placeholder="First Name"
                  aria-label="First Name"
                  required
                >
              `;
              emailField.parentNode.insertBefore(firstNameField, emailField);
            }
          }

          const submit = form.querySelector('button[type="submit"]');
          if (submit) submit.textContent = 'JOIN THE ART JOURNAL';

          form.addEventListener('submit', () => {
            const firstName = form.querySelector('input[name="contact[first_name]"]')?.value?.trim() || '';
            if (firstName) sessionStorage.setItem('artJournalFirstName', firstName);
            sessionStorage.setItem('artJournalPopupSubmitted', '1');
          });
        }

        actions.querySelector('.art-journal-popup__continue')?.addEventListener('click', () => {
          this.renderForm();
        });

        actions.querySelector('.art-journal-popup__decline')?.addEventListener('click', () => {
          this.hide();
        });

        this.renderInvite();
      }

      setTitle(content) {
        const heading = this.artJournal?.title?.querySelector('h1, h2, h3, h4, h5, h6');
        if (heading) heading.textContent = content;
      }

      renderInvite() {
        if (!this.artJournal) return;
        this.setTitle('Be the first to see new works');
        this.artJournal.textBlock.innerHTML = `
          <p>Join my Art Journal and I’ll let you know when I release a new artwork, with first public notification while the opening edition is available.</p>
          <p>I’ll also send you a complimentary copy of my art book, <em>Journeys on Water</em>.</p>
        `;
        this.artJournal.actions.hidden = false;
        this.artJournal.formWrap.hidden = true;
        this.artJournal.support.hidden = true;
      }

      renderForm() {
        if (!this.artJournal) return;
        this.setTitle('Welcome to my Art Journal');
        this.artJournal.textBlock.innerHTML = '<p>Where should I send your copy of <em>Journeys on Water</em> and my new-release notifications?</p>';
        this.artJournal.actions.hidden = true;
        this.artJournal.formWrap.hidden = false;
        this.artJournal.support.hidden = false;
        this.artJournal.formWrap.querySelector('input[name="contact[first_name]"]')?.focus();
      }

      hasSuccessfulSignup() {
        const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
        const submittedFromPopup = sessionStorage.getItem('artJournalPopupSubmitted') === '1';
        return posted && submittedFromPopup;
      }

      renderSuccess() {
        if (!this.artJournal) return;
        const firstName = sessionStorage.getItem('artJournalFirstName') || '';
        this.setTitle(firstName ? `Thank you, ${firstName}.` : 'Thank you.');
        this.artJournal.textBlock.innerHTML = `
          <p>Welcome to my Art Journal.</p>
          <p>Your copy of <em>Journeys on Water</em> is on its way to your inbox.</p>
          <p>I look forward to sharing what I discover on the water with you.</p>
          <p><strong>Ralph</strong></p>
        `;
        this.artJournal.actions.hidden = true;
        this.artJournal.formWrap.hidden = true;
        this.artJournal.support.hidden = true;

        const nativeAlert = this.querySelector('basic-modal.modal--newsletter-alert');
        if (nativeAlert) nativeAlert.hidden = true;

        sessionStorage.removeItem('artJournalFirstName');
        sessionStorage.removeItem('artJournalPopupSubmitted');
        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, 365);
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', () => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      copyToClipboard(code, button) {
        navigator.clipboard.writeText(code);
        button.innerText = button.dataset.copiedText;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}